import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { FinancialAidStaffDetailContext } from '../ApplicantDetail';
import { useUpdateFinancialAidApplication } from '../../../../financial-aid/hooks/useFinancialAidApi';
import { useShellProfile } from '../../../../state/useShellProfile';
import { asArray, asRecord } from '../../utils/domainData';
import {
  InternalNotesComposerCard,
  InternalNotesListSection,
  type InternalNoteTag,
  normalizeInternalNoteTag,
} from '../../../shared/internal-notes/InternalNotesShared';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';

const isDefined = <T,>(value: T | null | undefined): value is T => value != null;

const readText = (value: unknown, defaultValue = ''): string => {
  const next = String(value ?? '').trim();
  return next || defaultValue;
};

const toEpochMs = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return parsed > 1_000_000_000_000 ? parsed : parsed * 1000;
  }
  const parsedDate = Date.parse(readText(value));
  return Number.isFinite(parsedDate) ? parsedDate : null;
};

const formatNoteDate = (value: unknown): string => {
  const ms = toEpochMs(value);
  if (!ms) return 'Recent';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatNoteTime = (value: unknown): string => {
  const ms = toEpochMs(value);
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatComposerDate = (): string => {
  const today = new Date();
  const month = today.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = today.getDate();
  const year = today.getFullYear().toString().slice(-2);
  return `${month} ${day} '${year}`;
};

const formatTimelineLabel = (value: unknown): string => {
  const ms = toEpochMs(value);
  if (!ms) return 'RECENT';
  const now = new Date();
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = dayStart - 24 * 60 * 60 * 1000;
  if (ms >= dayStart) return 'TODAY';
  if (ms >= yesterdayStart) return 'YESTERDAY';
  return new Date(ms)
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    .toUpperCase();
};

const humanizeToken = (value: unknown): string =>
  readText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

type TimelineEvent = {
  id: string;
  label: string;
  title: string;
  detail: string;
  at: number;
  time: string;
  dotColor: string;
};

type InternalNotePayload = {
  id: string;
  author: string;
  createdBy?: string;
  createdAt: number;
  content: string;
  category: InternalNoteTag;
  tag: InternalNoteTag;
  important?: boolean;
};

const readCorrectionChangePath = (change: unknown): string => {
  const record = asRecord(change);
  return readText(record.path);
};

const PARENT_SUBMITTED_SNAPSHOT_PATH_PREFIX = 'additionalInfo.parentCollectedFinancialAidSnapshot';

const isParentSubmittedSnapshotPath = (path: string): boolean =>
  path === PARENT_SUBMITTED_SNAPSHOT_PATH_PREFIX
    || path.startsWith(`${PARENT_SUBMITTED_SNAPSHOT_PATH_PREFIX}.`);

const selectSubmittedCorrectionSourcePaths = (paths: string[]): string[] => {
  const submittedPaths = paths.filter(isParentSubmittedSnapshotPath);
  return submittedPaths.length > 0 ? submittedPaths : paths;
};

const countSubmittedCorrectionFields = (correction: Record<string, unknown>): number => {
  const changePaths = asArray<unknown>(correction.changes)
    .map(readCorrectionChangePath)
    .filter(Boolean);
  const submittedChangePaths = new Set(selectSubmittedCorrectionSourcePaths(changePaths));
  if (submittedChangePaths.size > 0) {
    return submittedChangePaths.size;
  }
  const changes = asArray<unknown>(correction.changes);
  if (changes.length > 0) {
    return changes.length;
  }
  const paths = asArray<unknown>(correction.paths)
    .map((path) => readText(path))
    .filter(Boolean);
  return new Set(selectSubmittedCorrectionSourcePaths(paths)).size || 1;
};

const toInternalNotePayload = (note: Record<string, unknown>, fallbackIndex: number): InternalNotePayload => {
  const createdAt =
    toEpochMs(note.createdAt)
    ?? toEpochMs(note.updatedAt)
    ?? toEpochMs(note.timestamp)
    ?? toEpochMs(note.date)
    ?? Date.now() - fallbackIndex;
  const author = readText(note.author, readText(note.createdBy, 'Staff Reviewer'));
  const createdBy = readText(note.createdBy);
  const category = normalizeInternalNoteTag(note.category ?? note.noteType ?? note.tag);
  const tag = normalizeInternalNoteTag(note.tag ?? note.category ?? note.noteType);
  return {
    id: readText(note.id, `note-${createdAt}-${fallbackIndex}`),
    author,
    ...(createdBy ? { createdBy } : {}),
    createdAt,
    content: readText(note.content, readText(note.note, readText(note.message))),
    category,
    tag,
    important: Boolean(note.important),
  };
};

const toErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'object' && error) {
    const record = error as Record<string, unknown>;
    const directMessage = record.message;
    if (typeof directMessage === 'string' && directMessage.trim()) {
      return directMessage;
    }
    const data = record.data;
    if (typeof data === 'object' && data) {
      const nestedMessage = (data as Record<string, unknown>).message;
      if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
        return nestedMessage;
      }
    }
  }
  const text = String(error ?? '').trim();
  return text && text !== '[object Object]' ? text : fallback;
};

export function NotesTab() {
  const ctx = useOutletContext<FinancialAidStaffDetailContext>();
  const { profile } = useShellProfile();
  const review = ctx.review;
  const history = ctx.statusHistory;
  const updateApplication = useUpdateFinancialAidApplication();
  const [noteDraft, setNoteDraft] = useState('');
  const [noteTag, setNoteTag] = useState<InternalNoteTag>('general');
  const [noteError, setNoteError] = useState<string | null>(null);

  const reviewRecord = asRecord(review as unknown);
  const applicationRecord = asRecord(reviewRecord.application);
  const sections = asRecord(applicationRecord.sections);
  const additionalInfo = asRecord(sections.additionalInfo);
  const sectionNotes = asRecord(sections.notes);
  const reviewNotes = asRecord(reviewRecord.notes);
  const profileRecord = asRecord(profile as unknown);
  const staffActorLabel = readText(
    profileRecord.displayName,
    readText(profileRecord.name, readText(profileRecord.email, readText(profileRecord.userSub, 'Staff Reviewer'))),
  );
  const staffActorId = readText(profileRecord.userSub, readText(profileRecord.email, staffActorLabel));

  const familyLabel = readText(applicationRecord.familyName, readText(applicationRecord.code, 'the family'));

  const internalNotes = useMemo(() => {
    const noteSources = [
      ...asArray<Record<string, unknown>>(additionalInfo.staffInternalNotes),
      ...asArray<Record<string, unknown>>(sectionNotes.internalNotes),
      ...asArray<Record<string, unknown>>(reviewNotes.internalNotes),
      ...asArray<Record<string, unknown>>(reviewRecord.notes),
    ];
    const normalized = noteSources
      .map((entry, index) => toInternalNotePayload(asRecord(entry), index))
      .filter((entry) => entry.content.length > 0);

    const deduped = new Map<string, InternalNotePayload>();
    for (const note of normalized) {
      const key = `${note.id}|${note.createdAt}|${note.content}`;
      if (!deduped.has(key)) {
        deduped.set(key, note);
      }
    }

    return Array.from(deduped.values()).sort((left, right) => right.createdAt - left.createdAt);
  }, [additionalInfo.staffInternalNotes, reviewNotes.internalNotes, reviewRecord.notes, sectionNotes.internalNotes]);

  const statusEntries = (history?.entries ?? []).filter(isDefined);
  const decisions = (Array.isArray(reviewRecord.decisionHistory) ? reviewRecord.decisionHistory : []).filter(isDefined);
  const uploadedDocuments = asArray<Record<string, unknown>>(reviewRecord.documents)
    .filter((doc) => Boolean(doc?.isUploaded))
    .map((doc, index) => ({
      id: readText(doc.documentId, `document-${index}`),
      at: toEpochMs(doc.uploadedAt) ?? 0,
      fileName: readText(doc.fileName, humanizeToken(doc.documentType) || 'Document'),
      uploadedBy: readText(doc.uploadedByName, readText(doc.uploadedBy, 'staff')),
    }));
  const staffCorrections = asArray<Record<string, unknown>>(additionalInfo.staffCorrections);

  const timeline = useMemo(() => {
    const events: TimelineEvent[] = [];

    const pushEvent = (entry: {
      id: string;
      title: string;
      detail: string;
      at: unknown;
      dotColor: string;
    }) => {
      const title = readText(entry.title);
      const detail = readText(entry.detail);
      const at = toEpochMs(entry.at) ?? 0;
      if (!title || !detail || !at) {
        return;
      }
      events.push({
        id: entry.id,
        title,
        detail,
        at,
        label: formatTimelineLabel(at),
        time: formatNoteTime(at),
        dotColor: entry.dotColor,
      });
    };

    const timelineSources = [
      ...asArray<Record<string, unknown>>(reviewRecord.timeline),
      ...asArray<Record<string, unknown>>(reviewNotes.activityTimeline),
      ...asArray<Record<string, unknown>>(sectionNotes.activityTimeline),
    ];
    for (const entry of timelineSources) {
      const record = asRecord(entry);
      const at = record.at ?? record.createdAt ?? record.date ?? record.changedAt;
      pushEvent({
        id: readText(record.id, `timeline-${events.length}`),
        title: readText(record.title, readText(record.eventType, 'Timeline Event')),
        detail: readText(record.detail, readText(record.description, 'Timeline update recorded.')),
        at,
        dotColor: readText(record.dotColor, '#404F4D'),
      });
    }

    for (const note of internalNotes) {
      pushEvent({
        id: `note-${note.id}`,
        title: 'New Note Added',
        detail: `${note.author} added a note to the internal log regarding this application.`,
        at: note.createdAt,
        dotColor: '#28E2A7',
      });
    }

    for (const [index, documentEntry] of uploadedDocuments.entries()) {
      pushEvent({
        id: `upload-${documentEntry.id}-${index}`,
        title: 'Document Uploaded',
        detail: `"${documentEntry.fileName}" was uploaded by ${documentEntry.uploadedBy}.`,
        at: documentEntry.at,
        dotColor: '#28E2A7',
      });
    }

    const sortedStaffCorrections = [...staffCorrections].sort((left, right) => {
      const leftAt = toEpochMs(asRecord(left).correctedAt) ?? 0;
      const rightAt = toEpochMs(asRecord(right).correctedAt) ?? 0;
      return rightAt - leftAt;
    });
    for (const [index, correction] of sortedStaffCorrections.entries()) {
      const correctionRecord = asRecord(correction);
      const correctedCount = countSubmittedCorrectionFields(correctionRecord);
      const fieldLabel = `${correctedCount} submitted field${correctedCount === 1 ? '' : 's'}`;
      const reason = readText(correctionRecord.reason, 'No reason recorded.');
      const actor = humanizeToken(correctionRecord.actorRole) || 'Staff';
      pushEvent({
        id: `staff-correction-${readText(correctionRecord.id, String(index))}`,
        title: 'Staff Correction Saved',
        detail: `${actor} saved corrections for ${fieldLabel}. Reason: ${reason}`,
        at: correctionRecord.correctedAt,
        dotColor: '#1F4D4D',
      });
    }

    for (const [index, entry] of statusEntries.entries()) {
      const statusText = humanizeToken(entry.status);
      pushEvent({
        id: `status-${readText(entry.status, 'unknown')}-${readText(entry.changedAt, String(index))}`,
        title: 'Status Changed',
        detail:
          readText(entry.note)
          || (statusText ? `Application moved to "${statusText}".` : 'Status update recorded by staff.'),
        at: entry.changedAt,
        dotColor: '#404F4D',
      });
    }

    for (const [index, decision] of (decisions as Array<Record<string, unknown>>).entries()) {
      const decisionType = humanizeToken(decision.decisionType);
      pushEvent({
        id: `decision-${readText(decision.id, String(index))}`,
        title: decisionType ? `Decision: ${decisionType}` : 'Decision Recorded',
        detail:
          readText(decision.overrideJustification)
          || readText(decision.denialExplanation)
          || readText(decision.denialReason)
          || 'Decision saved by staff reviewer.',
        at: decision.decidedAt,
        dotColor: '#FF5F5F',
      });
    }

    pushEvent({
      id: `submitted-${readText(applicationRecord.id, 'application')}`,
      title: 'Financial Aid Form Submitted',
      detail: 'The family has completed the primary financial assistance documentation.',
      at: applicationRecord.submittedAt,
      dotColor: '#FF5F5F',
    });

    pushEvent({
      id: `created-${readText(applicationRecord.id, 'application')}`,
      title: 'Application Started',
      detail: `Initial application portal access and profile created for ${familyLabel} family.`,
      at: applicationRecord.createdAt,
      dotColor: '#9CA3AF',
    });

    const deduped = new Map<string, TimelineEvent>();
    for (const event of events) {
      const key = `${event.title}|${event.detail}|${event.at}`;
      if (!deduped.has(key)) {
        deduped.set(key, event);
      }
    }

    return Array.from(deduped.values()).sort((left, right) => right.at - left.at);
  }, [
    applicationRecord.createdAt,
    applicationRecord.id,
    applicationRecord.submittedAt,
    decisions,
    familyLabel,
    internalNotes,
    reviewNotes.activityTimeline,
    reviewRecord.timeline,
    sectionNotes.activityTimeline,
    statusEntries,
    staffCorrections,
    uploadedDocuments,
  ]);

  const persistInternalNotes = async (notes: Array<Record<string, unknown>>) => {
    const nextInternalNotes = notes
      .map((note, index) => toInternalNotePayload(note, index))
      .filter((note) => note.content.length > 0);
    await updateApplication.mutateAsync({
      id: ctx.applicationId,
      sections: {
        additionalInfo: {
          staffInternalNotes: nextInternalNotes,
        },
      },
    });
    ctx.refetch();
  };

  const handleSaveNote = async () => {
    if (!ctx.applicationId) return;
    const content = noteDraft.trim();
    if (!content) return;
    setNoteError(null);
    setNoteDraft('');
    const nextNote = {
      id: `note-${Date.now()}`,
      author: staffActorLabel,
      createdBy: staffActorId,
      createdAt: Date.now(),
      content,
      category: noteTag,
      tag: noteTag,
      important: false,
    };

    try {
      await persistInternalNotes([nextNote, ...internalNotes]);
      setNoteTag('general');
    } catch (error) {
      setNoteDraft(content);
      setNoteError(toErrorMessage(error, 'Failed to save note'));
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!ctx.applicationId) return;
    setNoteError(null);
    try {
      await persistInternalNotes(internalNotes.filter((note) => readText(note.id) !== noteId));
    } catch (error) {
      setNoteError(toErrorMessage(error, 'Failed to delete note'));
    }
  };

  const handleCopyNote = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      // no-op: copy feedback is non-blocking
    }
  };

  return (
    <div data-testid="financial-aid-notes-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
      <section className="flex flex-col gap-8">
        <div>
          <h2 className="text-lg font-bold text-[#111827] mb-4">Internal Notes</h2>
          <InternalNotesComposerCard
            noteText={noteDraft}
            onNoteTextChange={setNoteDraft}
            noteTag={noteTag}
            onNoteTagChange={setNoteTag}
            placeholder="Start typing a private note about this application..."
            currentDateLabel={formatComposerDate()}
            onSave={handleSaveNote}
            isSaving={updateApplication.isPending}
            isDisabled={updateApplication.isPending}
          />
          {noteError ? (
            <SupportErrorMessage className="mt-3">
              {noteError}
            </SupportErrorMessage>
          ) : null}
        </div>

        <InternalNotesListSection
          notes={internalNotes.map((note, index) => {
            const noteId = readText(note.id, `note-${index}`);
            const noteText = readText(note.content);
            return {
              id: noteId,
              authorName: readText(note.author, 'Admin User'),
              createdAtLabel: formatNoteDate(note.createdAt),
              content: noteText,
              tag: note.tag,
              important: note.important,
              onCopy: () => handleCopyNote(noteText),
              onDelete: () => handleDeleteNote(noteId),
            };
          })}
          emptyMessage="No internal notes yet."
        />
      </section>

      <div className="lg:border-l lg:pl-8 border-gray-100">
        <h2 className="text-lg font-bold text-[#111827] mb-6">Activity Timeline</h2>
        <div data-testid="financial-aid-notes-timeline" className="relative pl-2">
          <div className="absolute left-[5px] top-2 bottom-0 w-0.5 bg-[#E5E7EB]" />
          <div className="space-y-0">
            {timeline.length === 0 ? (
              <p className="text-sm text-[#6B7280]">No timeline events yet.</p>
            ) : null}
            {timeline.map((event) => (
              <div key={event.id} data-testid="financial-aid-notes-timeline-event" className="flex gap-4 pb-8 relative group">
                <div className="w-3 h-3 rounded-full mt-1.5 z-10 flex-shrink-0 ring-4 ring-[#FAFBF8]" style={{ backgroundColor: event.dotColor }} />
                <div>
                  <span className="text-xs font-semibold tracking-wider uppercase text-[#9CA3AF]">{event.label}</span>
                  <p className="text-sm font-semibold text-[#111827] mt-1">{event.title}</p>
                  <p className="text-sm text-[#6B7280] mb-1">{event.detail}</p>
                  <span className="text-xs text-[#9CA3AF]">{event.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
